import { useReducer } from "react"

import { UserSortField, SortDirection, type GetUsersQuery } from "@/graphql/graphql"

export type User = NonNullable<NonNullable<GetUsersQuery["users"]>["collection"]>[number]

type DialogKind = "create" | "edit" | "delete" | null

export interface UsersState {
  sortBy: UserSortField
  sortDirection: SortDirection
  dialog: DialogKind
  selectedUser: User | null
  successMessage: string | null
  errorMessage: string | null
}

export type UsersAction =
  | { type: "TOGGLE_SORT"; field: UserSortField }
  | { type: "OPEN_CREATE" }
  | { type: "OPEN_EDIT"; user: User }
  | { type: "OPEN_DELETE"; user: User }
  | { type: "CLOSE_DIALOG" }
  | { type: "SET_SUCCESS"; message: string }
  | { type: "SET_ERROR"; message: string }
  | { type: "CLEAR_MESSAGES" }

const initialState: UsersState = {
  sortBy: UserSortField.Name,
  sortDirection: SortDirection.Asc,
  dialog: null,
  selectedUser: null,
  successMessage: null,
  errorMessage: null,
}

function reducer(state: UsersState, action: UsersAction): UsersState {
  switch (action.type) {
    case "TOGGLE_SORT":
      if (state.sortBy === action.field) {
        return {
          ...state,
          sortDirection: state.sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc,
        }
      }
      return { ...state, sortBy: action.field, sortDirection: SortDirection.Asc }
    case "OPEN_CREATE":
      return { ...state, dialog: "create" }
    case "OPEN_EDIT":
      return { ...state, dialog: "edit", selectedUser: action.user }
    case "OPEN_DELETE":
      return { ...state, dialog: "delete", selectedUser: action.user }
    case "CLOSE_DIALOG":
      return { ...state, dialog: null, selectedUser: null }
    case "SET_SUCCESS":
      return { ...state, successMessage: action.message, errorMessage: null }
    case "SET_ERROR":
      return { ...state, errorMessage: action.message, successMessage: null }
    case "CLEAR_MESSAGES":
      return { ...state, successMessage: null, errorMessage: null }
    default:
      return state
  }
}

export const useUsersState = () => useReducer(reducer, initialState)
